import type { PermanentBuildingKind } from '@signalclash/shared';

export type BuildModeListener = (active: PermanentBuildingKind | null) => void;

// Client-side build-mode state. The simulation does not care which build button
// the player has armed — it's a presentation/intent concern. BuildPanel writes
// the active kind; InputRouter reads it on left-click to translate (cursor) into
// a BuildCommand; Renderer reads it to draw slot ghosts. Restricted to
// PermanentBuildingKind because mini_turret spawns only via the Engineer's Q
// ability — never via the Build panel.
export class BuildMode {
  private active: PermanentBuildingKind | null = null;
  private listeners = new Set<BuildModeListener>();

  arm(kind: PermanentBuildingKind): void {
    if (this.active === kind) {
      this.cancel();
      return;
    }
    this.active = kind;
    this.notify();
  }

  cancel(): void {
    if (this.active === null) return;
    this.active = null;
    this.notify();
  }

  toggleDefault(): void {
    if (this.active !== null) {
      this.cancel();
      return;
    }
    this.arm('production_hub');
  }

  // V5++ — cycle through every permanent build kind on repeated taps:
  //   idle → production_hub → turret → idle
  // Used by the MobileActionBar's Build button so a touch player can pick
  // Hub or Turret without leaving the bar (previously they had to find the
  // Hub/Turret picker inside the floating HUD context panel). Desktop's
  // keyboard B path still uses toggleDefault — cycle behaviour is a
  // mobile-side affordance, not a sim concern.
  cycleDefault(): void {
    if (this.active === null) {
      this.arm('production_hub');
      return;
    }
    if (this.active === 'production_hub') {
      // arm() toggles off when called with the current kind, so we set the
      // field directly + notify here instead of going through arm('turret').
      this.active = 'turret';
      this.notify();
      return;
    }
    this.cancel();
  }

  getActive(): PermanentBuildingKind | null {
    return this.active;
  }

  isArmed(): boolean {
    return this.active !== null;
  }

  subscribe(listener: BuildModeListener): () => void {
    this.listeners.add(listener);
    listener(this.active);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify(): void {
    for (const fn of this.listeners) fn(this.active);
  }
}
